/**
 * kadSalinan.js — dua pagar untuk kad yang BERKONGSI sesuatu dengan kad lain (2026-10-06).
 *
 * Duplicate (WorkspaceView.handleDuplicateProducts) membina kad salinan dengan `{ ...kadAsal }`,
 * jadi salinan mewarisi SEMUA medan kad asal kecuali yang ditimpa satu per satu. Audit 6 Okt
 * menemui dua warisan yang salah:
 *
 * (1) STATUS LARIAN — `setStatusAkaun` (pil Posted / Scheduled / Failed per akaun TikTok/FB) dan
 *     `setStatusReason` (sebab gagal) ikut tersalin, sedangkan hanya `setStatuses` yang dikosongkan.
 *     Kad salinan yang belum ada video dan belum pernah di-post memapar pil "Posted" kad asal.
 *     → `buangStatusLarian`.
 *
 * (2) FAIL GAMBAR DIKONGSI — gambar yang agent hasilkan disimpan di bawah laluan kad ASAL
 *     (`users/{uid}/products/{idAsal}/agent_*`, lihat utils/agentGambar.js) dan kad salinan
 *     merujuk URL yang SAMA. Padam pukal kad asal memadam fail itu → gambar tambahan kad salinan
 *     pecah. → `failDirujukKadKekal` + `tapisFailMasihDipakai`: fail yang masih dirujuk mana-mana
 *     kad yang KEKAL tidak dipadam. Pagar ini hanya MENGURANGKAN apa yang dipadam, tidak pernah
 *     menambah — kad yang tidak berkongsi apa-apa dipadam tepat seperti dahulu.
 *
 * ⚠️ Had yang diketahui: hanya KAD yang disemak. Item My Products yang merujuk fail kad (boleh
 *    berlaku selepas "Semua kad + My Products" menolak gambar agent ke item) TIDAK disemak —
 *    membaca semua item pelanggan pada setiap padam terlalu mahal.
 * ⚠️ Fail yang ditahan tidak dipadam kemudian oleh kad salinan (ia bukan di laluan kad itu);
 *    Optimize yang membersihkannya bila tiada kad merujuknya lagi.
 *
 * Fungsi TULEN — tiada Firestore, tiada React. Diuji dalam Node.
 */

/** Medan status larian peringkat kad yang kad salinan TIDAK boleh warisi.
 *  (`setStatuses` sudah di-null oleh Duplicate sendiri.) */
export const MEDAN_STATUS_LARIAN = Object.freeze(['setStatusAkaun', 'setStatusReason']);

/** Buang status larian daripada objek kad salinan (mengubah objek itu; memulangkannya semula).
 *  DIBUANG, bukan di-null: kad salinan = doc BAHARU, jadi ia lahir tanpa medan ini — sama
 *  seperti kad yang baru ditarik dari My Products. */
export function buangStatusLarian(kad) {
    if (!kad || typeof kad !== 'object') return kad;
    for (const k of MEDAN_STATUS_LARIAN) delete kad[k];
    return kad;
}

/** Kunci perbandingan fail: laluan Storage (tanpa token / query) untuk URL Firebase Storage;
 *  selain itu, URL itu sendiri. Dua URL fail yang sama dengan token berbeza = kunci sama. */
export function kunciFail(url) {
    const u = typeof url === 'string' ? url.trim() : '';
    if (!u) return '';
    if (u.includes('firebasestorage.googleapis.com')) {
        try {
            const m = new URL(u).pathname.match(/\/o\/(.+)$/);
            if (m) return decodeURIComponent(m[1]);
        } catch { /* bukan URL sah → banding teks */ }
    }
    return u;
}

/* ⚠️ KEMBAR: `DUP_MEDIA_FIELDS` / `PROMPT_VIEW_SKIP` (WorkspaceView.jsx) dan `MEDIA_SET`
   (utils/kosongkanPrompt.js) — medan media per set. Medan media baharu → tambah di semua. */
const MEDAN_MEDIA_SET = ['uploadedVideo', 'uploadedVideoThumb', 'uploadedImage'];

/**
 * Semua fail yang dirujuk kad-kad yang KEKAL selepas padam: gambar utama, gambar tambahan,
 * dan media setiap set. Medan media duduk dalam doc UTAMA kad (Split V2 hanya memindahkan
 * teks), jadi senarai kad dalam ingatan sudah memadai — tiada bacaan pangkalan data.
 * @param {object[]} semuaKad   semua kad pelanggan (sebelum padam)
 * @param {Set<string>|string[]} idDipadam
 * @returns {Set<string>} kunci fail (lihat `kunciFail`)
 */
export function failDirujukKadKekal(semuaKad, idDipadam) {
    const dipadam = idDipadam instanceof Set ? idDipadam : new Set(idDipadam || []);
    const kekal = new Set();
    const tambah = (u) => { const k = kunciFail(u); if (k) kekal.add(k); };
    for (const p of (Array.isArray(semuaKad) ? semuaKad : [])) {
        if (!p || dipadam.has(p.id)) continue;
        tambah(p.imageUrl);
        // `imageBase64` kadang-kadang membawa URL (susun gambar menulis kedua-dua medan); base64 mentah dilangkau.
        if (typeof p.imageBase64 === 'string' && /^https?:\/\//i.test(p.imageBase64)) tambah(p.imageBase64);
        if (Array.isArray(p.extraImageUrls)) p.extraImageUrls.forEach(tambah);
        const pr = p.prompts && typeof p.prompts === 'object' ? p.prompts : {};
        for (const s of Object.values(pr)) {
            if (!s || typeof s !== 'object' || Array.isArray(s)) continue;
            for (const m of MEDAN_MEDIA_SET) tambah(s[m]);
        }
    }
    return kekal;
}

/** Buang daripada senarai padam mana-mana fail yang kad KEKAL masih rujuk. */
export function tapisFailMasihDipakai(urls, kekal) {
    const k = kekal instanceof Set ? kekal : new Set();
    return (Array.isArray(urls) ? urls : []).filter((u) => !k.has(kunciFail(u)));
}
